<template>
  <div>
    <PageAllow controller="Districts" action="index" />
    <Breadcrumbs :items="items" />

    <v-sheet class="transparent-background-color">
      <v-row dense class="mx-5">
        <v-col cols="12" sm="12" md="4">
          <v-row dense align="center" class="pa-1">
            <v-text-field
              v-model="districtsSearch"
              :label="$t('District.districtsKeyword')"
              clearable
            ></v-text-field>
          </v-row>
        </v-col>
        <v-col cols="12" sm="12" md="8" align-self="center">
          <v-row dense align="center">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="px-0 py-0 caption font-weight-bold grey--text"
                  color="primary"
                  @click.stop="getDistrictsFromAPI()"
                >
                  <v-icon small class="pe-1">fa-search</v-icon>
                  {{ $t("Search.search") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.searchTip") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="caption font-weight-bold grey--text"
                  @click.stop="districtsSearch = ''"
                >
                  <v-icon x-small class="pe-1">fa-trash-alt</v-icon>
                  {{ $t("Search.reset") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.resetTip") }}</span>
            </v-tooltip>
            <v-spacer />
            <v-btn
              v-if="$permission.permissionAllow('Districts', 'add')"
              small
              color="primary"
              @click.stop="
                currentItem = { id: 0 };
                editDialog = true;
              "
              class="mt-6"
            >
              <v-icon x-small class="pe-1">fa-plus</v-icon>
              {{ $t("CRUD.newInfo", [$t("District.ObjectName")]) }}
            </v-btn>
          </v-row>
        </v-col>
      </v-row>
      <v-card class="mx-4 mb-4">
        <v-progress-linear :indeterminate="loading"></v-progress-linear>
        <v-btn text x-large color="primary" @click.stop="openAll = !openAll">
          {{ $t("openAll") }} / {{ $t("closeAll") }}
        </v-btn>
        <v-divider />
        <v-treeview
          class="py-2"
          v-model="tree"
          :items="districts"
          :search="districtsSearch"
          :filter="districtsFilter"
          item-key="code"
          item-children="districts"
          :open="districtsOpen"
          hoverable
          activatable
          return-object
        >
          <template v-slot:label="{ item }">
            <v-chip> {{ item.number }} </v-chip>
            {{ item.name }}

            <v-icon
              small
              v-if="$permission.permissionAllow('Districts', 'edit')"
              color="primary"
              class="px-1 py-4 me-1"
              @click.stop="
                currentItem = JSON.parse(JSON.stringify(item));
                editDialog = true;
              "
              >fa-pencil-alt
            </v-icon>
            <v-icon
              small
              v-if="$permission.permissionAllow('Districts', 'add')"
              color="primary"
              class="px-1 py-4 me-1"
              @click.stop="
                currentItem = {
                  parent_id: item.id,
                  parent_number: item.number,
                  parent_name: item.name,
                };
                editDialog = true;
              "
              >fa-plus
            </v-icon>
            <v-icon
              small
              v-if="
                item.deletable &&
                $permission.permissionAllow('Districts', 'delete')
              "
              color="accent"
              class="px-1 py-4 me-1"
              @click.stop="
                currentItem = JSON.parse(JSON.stringify(item));
                deleteDialog = true;
              "
              >fa-trash-alt
            </v-icon>
          </template>
        </v-treeview>
      </v-card>
    </v-sheet>

    <v-dialog persistent v-model="editDialog" width="600px">
      <v-card>
        <v-card-title>
          <span class="headline">{{ $t("District.ObjectNameInfo") }}</span>

          <v-spacer></v-spacer>
          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" @click="editDialog = false"
                >fa-times
              </v-icon>
            </template>
            <span>
              {{ $t("CRUD.backToList", [$t("District.ObjectName")]) }}
            </span>
          </v-tooltip>
        </v-card-title>
        <v-card-text>
          <v-container>
            <ValidationObserver ref="observer">
              <v-form>
                <v-row>
                  <v-col
                    cols="12"
                    v-if="currentItem.parent_id && !currentItem.id"
                  >
                    <v-text-field
                      required
                      readonly
                      :label="$t('District.Fields.parent_number_and_name')"
                      :value="
                        currentItem.parent_number +
                        ' / ' +
                        currentItem.parent_name
                      "
                    >
                    </v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <ValidationProvider
                      v-slot="{ errors }"
                      :name="$t('District.Fields.district_number')"
                      rules="required"
                    >
                      <v-text-field
                        required
                        v-model="currentItem.number"
                        :error-messages="
                          $resparse
                            .getErrorMsgs(serverErrors, 'number')
                            .concat(errors)
                        "
                      >
                        <template v-slot:label>
                          {{ $t("District.Fields.district_number") }}
                          <span class="red--text">*</span>
                        </template>
                      </v-text-field>
                    </ValidationProvider>
                  </v-col>
                  <v-col cols="12">
                    <ValidationProvider
                      v-slot="{ errors }"
                      :name="$t('District.Fields.district_name')"
                      rules="required"
                    >
                      <v-text-field
                        required
                        v-model="currentItem.name"
                        :error-messages="
                          $resparse
                            .getErrorMsgs(serverErrors, 'name')
                            .concat(errors)
                        "
                      >
                        <template v-slot:label>
                          {{ $t("District.Fields.district_name") }}
                          <span class="red--text">*</span>
                        </template>
                      </v-text-field>
                    </ValidationProvider>
                  </v-col>
                  <v-col cols="12">
                    <v-text-field required v-model="currentItem.sort">
                      <template v-slot:label>
                        {{ $t("District.Fields.district_sort") }}
                      </template>
                    </v-text-field>
                  </v-col>
                </v-row>
              </v-form>
            </ValidationObserver>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" outlined @click="editDialog = false">{{
            $t("$vuetify.close")
          }}</v-btn>
          <v-btn color="accent" @click="save()">{{ $t("CRUD.save") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <DialogDelete
      v-model="deleteDialog"
      :label="$t('District.ObjectName')"
      @confirm="
        del();
        deleteDialog = false;
      "
    />
  </div>
</template>

<script>
import i18n from "@/i18n";
import { mapState } from "vuex";
import PageAllow from "@/components/PageAllow";
import Breadcrumbs from "@/components/Breadcrumbs";
import Districts from "@/api/districts";
import DialogDelete from "@/components/plugins/DialogDelete";
import {
  ValidationObserver,
  ValidationProvider,
  setInteractionMode,
} from "vee-validate";

setInteractionMode("eager");

export default {
  components: {
    PageAllow,
    Breadcrumbs,
    ValidationProvider,
    ValidationObserver,
    DialogDelete,
  },

  computed: {
    items() {
      return [
        {
          text: i18n.t("Nav.home"),
          disabled: false,
          href: "/home",
        },
        {
          text: i18n.t("Nav.shopManagement", [this.$consts.shopName()]),
          disabled: false,
          href: "/shops",
        },
        {
          text: i18n.t("Nav.districts"),
          disabled: true,
          href: "/districts",
        },
      ];
    },
    districtsFilter() {
      return (item, search) =>
        item.name.indexOf(search) > -1 || item.number.indexOf(search) > -1;
    },
    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState(["loading"]),
  },

  mounted() {
    this.getDistrictsFromAPI();
  },
  data: () => ({
    editDialog: false,
    deleteDialog: false,
    tree: [],
    districts: [],
    districtsSearch: "",
    districtsSelected: [],
    currentItem: {},
    serverErrors: [],
    openAll: false,
    districtsOpen: [],
  }),

  methods: {
    openTree(openArr) {
      for (var i = 0; i < openArr.length; i++) {
        this.districtsOpen.push(openArr[i]);
        if (openArr[i].districts.length > 0) {
          this.openTree(openArr[i].districts);
        }
      }
    },
    getDistrictsFromAPI() {
      Districts.tree().then(
        function (response) {
          if (response.data.status.isOK()) {
            this.districts = response.data.districts;
          }
        }.bind(this)
      );
    },

    save() {
      this.$refs.observer.validate().then(
        function (result) {
          if (result) {
            Districts.save(this.currentItem.id, this.currentItem)
              .then(
                function (response) {
                  if (response.data.status.isOK()) {
                    this.$emit("save", this.currentItem);
                    this.getDistrictsFromAPI();
                    this.editDialog = false;
                    this.$store.dispatch("fullscreenLoadingStop");
                  } else if (
                    response.data.error &&
                    response.data.error.data &&
                    "ERR333" == response.data.error.code
                  ) {
                    this.serverErrors = response.data.error.data;
                  }
                }.bind(this)
              )
              .catch(
                function () {
                  this.editDialog = false;
                  this.$store.dispatch("fullscreenLoadingStop");
                }.bind(this)
              );
          }
        }.bind(this)
      );
    },

    del() {
      Districts.del(this.currentItem.id).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.getDistrictsFromAPI();
          }
        }.bind(this)
      );
    },
  },

  watch: {
    editDialog: {
      handler() {
        if (!this.editDialog) {
          this.$refs.observer.reset();
          this.serverErrors = [];
          this.$store.dispatch("fullscreenLoadingStop");
        }
      },
    },
    openAll: {
      handler() {
        if (this.openAll) {
          this.openTree(this.districts);
        } else {
          this.districtsOpen = [];
        }
      },
    },
  },
};
</script>